import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Image, Popover, PopoverHeight, PopoverType, ScrollableList, ScrollableListVariant } from '@as/cotopaxi';
import { BUCKET_FILTER, MULTISELECT_FILTER, } from '@as-react/common-types';
import { Box, Stack, TextAlpha, useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Button, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { analyticsActions } from '../../../../redux/analytics/actions/AnalyticsActions.creators';
import { getBucketFilter, getFilterQueryParameter, getMultiFilter } from '../../../../util/FilterUtil';
import { generateTestId } from '../../../../util/TestUtil';
import { FilterWizardItem } from './FilterWizardItem';
const TEST_ID = 'filter-wizard-modal';
export const FilterWizardModal = ({ anchorEl, fetchSearchResultsCount, filter, filterWizardDescription, filterWizardImage, filterWizardTitle, filterWizardValues, handlePopoverClose, itemsCount, updateFilters, urlFilters, activeWizardFilterKeys, setActiveWizardFilterKeys, }) => {
    const [hasFilterUpdated, setFilterUpdated] = useState(false);
    const isMobile = useMediaQueryMatchesOnMobile();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const dispatch = useDispatch();
    const getFilterQueryParams = (tmpSelectedFilterKeys, filterOn, filterType) => {
        var _a;
        const defaultFilter = urlFilters.defaultFilter ? getFilterQueryParameter(urlFilters.defaultFilter) : '';
        const appliedFilters = ((_a = urlFilters.filter) === null || _a === void 0 ? void 0 : _a.length)
            ? urlFilters.filter.reduce((query, tmpFilter) => tmpFilter.filterOn === filterOn
                ? query
                : query + getFilterQueryParameter(`${tmpFilter.filterOn}:${tmpFilter.filterValue}`), '')
            : '';
        const wizardFilters = tmpSelectedFilterKeys.length
            ? filterType === MULTISELECT_FILTER
                ? getMultiFilter(filterOn, tmpSelectedFilterKeys)
                : getBucketFilter(filterOn, tmpSelectedFilterKeys[0])
            : '';
        return `${defaultFilter}${appliedFilters}${wizardFilters}`.substring(1); // remove '&' from beginning of the string
    };
    const updateResultsCount = (query) => {
        fetchSearchResultsCount(query);
    };
    const onSelectChange = (filterKey) => {
        let newActiveWizardFilterKeys = [];
        if (filter.type === MULTISELECT_FILTER) {
            const selectedValueIndex = activeWizardFilterKeys.indexOf(filterKey);
            if (selectedValueIndex === -1) {
                newActiveWizardFilterKeys = [...activeWizardFilterKeys, filterKey];
            }
            else {
                newActiveWizardFilterKeys = [...activeWizardFilterKeys];
                newActiveWizardFilterKeys.splice(selectedValueIndex, 1);
            }
        }
        else {
            if (!activeWizardFilterKeys.includes(filterKey)) {
                newActiveWizardFilterKeys.push(filterKey);
            }
        }
        setActiveWizardFilterKeys(newActiveWizardFilterKeys);
        setFilterUpdated(true);
        updateResultsCount(getFilterQueryParams(newActiveWizardFilterKeys, filter.filterOn, filter.type));
    };
    const applyFilters = () => {
        const filterType = filter.type === BUCKET_FILTER ? BUCKET_FILTER : MULTISELECT_FILTER;
        updateFilters({
            filterOn: filter.filterOn,
            filterOnName: filter.name,
            filteredItems: filter.values.map((filterValue) => ({
                filterKey: filterValue.filterKey,
                filterKeyName: filterValue.name,
                filterOn: filter.filterOn,
                filterOnName: filter.name,
                filterType: filterType,
                isActive: activeWizardFilterKeys.includes(filterValue.filterKey),
            })),
            filterType: filterType,
            filterDisplayHint: filter.displayHint,
            filterContentType: filter.filterContentType,
            multiUpdate: true,
        });
        // Only call when user made changes to the filters.
        hasFilterUpdated && dispatch(analyticsActions.filterWizardSelected(filter.filterOn));
        handlePopoverClose();
    };
    useEffect(() => {
        window.addEventListener('popstate', handlePopoverClose);
        return () => window.removeEventListener('popstate', handlePopoverClose);
    }, []);
    if (!filter.values) {
        return null;
    }
    return (_jsx(Popover, { anchorEl: anchorEl, type: !isDesktop ? PopoverType.FILTERS_WIZARD : PopoverType.MODAL, onClose: handlePopoverClose, height: PopoverHeight.LARGE, title: filterWizardTitle || filter.name, footerElement: _jsx(Box, { paddingInline: { md: '4' }, children: _jsx(Stack, { justify: "end", children: _jsx(Button, { fullWidth: isMobile, onClick: applyFilters, children: t('filterWizard.button.showResults', {
                        count: itemsCount,
                    }) }) }) }), dataQA: generateTestId({ testId: TEST_ID, suffix: 'popover' }), children: _jsx(ScrollableList, { variant: ScrollableListVariant.SHADOW, children: _jsxs(Box, { paddingInline: { base: '4', md: '8' }, testId: generateTestId({ testId: TEST_ID, suffix: 'list' }), children: [(!!filterWizardDescription || !!filterWizardImage) && (_jsxs(Box, { paddingBlockEnd: "2", children: [!!filterWizardImage && (_jsx(Box, { paddingBlockEnd: { base: '2', md: '4' }, children: _jsx(Image, { src: filterWizardImage, lazy: true, dataQA: generateTestId({ testId: TEST_ID, suffix: 'image' }) }) })), !!filterWizardDescription && (_jsx(Box, { paddingBlockEnd: { base: '2', md: '4' }, children: _jsx(TextAlpha, { as: "span", variant: "bodyS", testId: generateTestId({ testId: TEST_ID, suffix: 'description' }), children: filterWizardDescription }) }))] })), filter.values.map((filterValue) => {
                        const filterWizardValue = filterWizardValues.find((filerWizardValue) => filerWizardValue.filterKey === filterValue.filterKey);
                        const isSelected = activeWizardFilterKeys.includes(filterValue.filterKey);
                        const _onSelectChange = () => onSelectChange(filterValue.filterKey);
                        return (_jsx(FilterWizardItem, { checked: isSelected, filterKey: filterValue.filterKey, title: (filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.title) || filterValue.name, itemCount: filterValue.itemCount, imageURL: filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.image, description: filterWizardValue === null || filterWizardValue === void 0 ? void 0 : filterWizardValue.description, onChange: _onSelectChange, multiSelect: filter.type === MULTISELECT_FILTER, testId: generateTestId({ testId: TEST_ID, suffix: filterValue.filterKey }) }, filterValue.filterKey));
                    })] }) }) }));
};
